import React, { useState, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Search, Download, Archive, ArchiveRestore, Mail, MailOpen,
  StickyNote, Save, ChevronDown, ChevronUp,
} from "lucide-react";
import { API } from "@/lib/config";

const STATUSES = ["New", "In Progress", "Contacted", "Approved", "Closed"];

const STATUS_STYLES = {
  New: "bg-[#FFF5F9] text-[#E94F9A]",
  "In Progress": "bg-amber-100 text-amber-800",
  Contacted: "bg-blue-100 text-blue-800",
  Approved: "bg-green-100 text-green-800",
  Closed: "bg-gray-200 text-gray-600",
};

const toCSV = (rows, columns) => {
  const header = columns.map((c) => `"${c.label}"`).join(",");
  const lines = rows.map((r) =>
    columns
      .map((c) => {
        const v = c.value(r);
        return `"${String(v ?? "").replace(/"/g, '""')}"`;
      })
      .join(",")
  );
  return [header, ...lines].join("\n");
};

const fmtDate = (ts) => {
  if (!ts) return "—";
  try {
    return new Date(ts).toLocaleString(undefined, {
      year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return String(ts);
  }
};

export const InquiryManager = ({
  title,
  testId,
  collection,
  items = [],
  onChanged,
  primary,
  subtitle,
  badges,
  details = [],
  searchKeys = [],
  csvColumns = [],
  extraActions,
}) => {
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [sortOrder, setSortOrder] = useState("newest");
  const [showArchived, setShowArchived] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [noteDraft, setNoteDraft] = useState({});

  const patch = async (item, body) => {
    try {
      await axios.patch(`${API}/admin/inquiry/${collection}/${item.id}`, body);
      onChanged && onChanged();
    } catch (err) {
      toast.error("Update failed. Please try again.");
    }
  };

  const markRead = (item, read) => patch(item, { read });
  const setStatus = (item, status) => patch(item, { status }).then(() => toast.success(`Status set to ${status}`));
  const toggleArchive = (item) => patch(item, { archived: !item.archived }).then(() => toast.success(item.archived ? "Restored" : "Archived"));
  const saveNote = (item) => patch(item, { notes: noteDraft[item.id] ?? item.notes ?? "" }).then(() => toast.success("Note saved"));

  const filtered = useMemo(() => {
    let list = items.filter((i) => (showArchived ? i.archived : !i.archived));
    if (statusFilter !== "All") list = list.filter((i) => (i.status || "New") === statusFilter);
    if (query.trim()) {
      const q = query.toLowerCase();
      list = list.filter((i) =>
        [...searchKeys, "notes"].some((k) => String(i[k] ?? "").toLowerCase().includes(q))
      );
    }
    list = [...list].sort((a, b) => {
      const da = new Date(a.timestamp || 0).getTime();
      const db = new Date(b.timestamp || 0).getTime();
      return sortOrder === "newest" ? db - da : da - db;
    });
    return list;
  }, [items, showArchived, statusFilter, query, sortOrder, searchKeys]);

  const unreadCount = items.filter((i) => !i.read && !i.archived).length;

  const exportCSV = () => {
    if (!filtered.length) return toast.info("Nothing to export.");
    const cols = csvColumns.length ? csvColumns : [
      { label: "Date", value: (r) => r.timestamp },
      { label: "Status", value: (r) => r.status || "New" },
      { label: "Notes", value: (r) => r.notes },
    ];
    const csv = toCSV(filtered, cols);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `agg_${collection}_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast.success("Exported CSV");
  };

  return (
    <div className="bg-white border border-[#E5E5E5]/50 rounded-2xl p-6 md:p-8 space-y-5" data-testid={`panel-${testId}`}>
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div>
          <h3 className="font-serif text-lg font-bold text-[#4B1E5A]">{title}</h3>
          <p className="font-sans text-xs text-[#4A4A4A]">
            {filtered.length} {showArchived ? "archived" : "active"}
            {unreadCount > 0 && !showArchived && (
              <span className="ml-2 text-[#E94F9A] font-semibold">• {unreadCount} unread</span>
            )}
          </p>
        </div>
        <button
          onClick={exportCSV}
          data-testid={`export-${testId}-btn`}
          className="inline-flex items-center gap-1.5 border border-[#E5E5E5] text-[#4A4A4A] hover:bg-[#FFF5F9] text-xs px-3.5 py-2 rounded-full font-semibold self-start lg:self-auto"
        >
          <Download className="h-3.5 w-3.5" /> Export CSV
        </button>
      </div>

      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row flex-wrap gap-3">
        <div className="relative flex-grow min-w-[180px]">
          <Search className="h-4 w-4 text-[#4A4A4A] absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search..."
            data-testid={`search-${testId}`}
            className="w-full bg-white border border-[#E5E5E5] rounded-xl pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#E94F9A]/30"
          />
        </div>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid={`filter-status-${testId}`} className="bg-white border border-[#E5E5E5] rounded-xl px-3 py-2 text-sm">
          <option value="All">All Statuses</option>
          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={sortOrder} onChange={(e) => setSortOrder(e.target.value)} data-testid={`sort-${testId}`} className="bg-white border border-[#E5E5E5] rounded-xl px-3 py-2 text-sm">
          <option value="newest">Newest First</option>
          <option value="oldest">Oldest First</option>
        </select>
        <button
          onClick={() => setShowArchived((v) => !v)}
          data-testid={`toggle-archived-${testId}`}
          className={`inline-flex items-center gap-1.5 rounded-xl px-3.5 py-2 text-xs font-semibold border transition-all ${showArchived ? "bg-[#4B1E5A] text-white border-[#4B1E5A]" : "bg-white text-[#4A4A4A] border-[#E5E5E5] hover:bg-[#FFF5F9]"}`}
        >
          <Archive className="h-3.5 w-3.5" /> {showArchived ? "Viewing Archived" : "Show Archived"}
        </button>
      </div>

      {/* List */}
      <div className="space-y-3">
        {filtered.length === 0 && (
          <p className="text-sm text-[#4A4A4A] text-center py-10">No submissions found.</p>
        )}
        {filtered.map((item) => {
          const isOpen = !!expanded[item.id];
          return (
            <div
              key={item.id}
              data-testid={`${testId}-item-${item.id}`}
              className={`border rounded-xl p-4 transition-all ${item.read ? "border-[#E5E5E5]" : "border-[#E94F9A]/40 bg-[#FFF5F9]/10"}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-2.5 min-w-0">
                  {!item.read && <span className="h-2 w-2 rounded-full bg-[#E94F9A] mt-2 flex-shrink-0" title="Unread" />}
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <h4 className="font-serif font-bold text-sm text-[#4B1E5A]">{primary(item)}</h4>
                      {badges && badges(item)}
                    </div>
                    <p className="text-xs text-[#4A4A4A] font-sans truncate">{subtitle(item)}</p>
                    <p className="text-[11px] text-[#9b9b9b] font-sans mt-0.5">{fmtDate(item.timestamp)}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className={`px-2.5 py-1 rounded-full text-[10px] font-semibold ${STATUS_STYLES[item.status] || STATUS_STYLES.New}`}>{item.status || "New"}</span>
                  <button onClick={() => setExpanded((e) => ({ ...e, [item.id]: !isOpen }))} data-testid={`expand-${testId}-${item.id}`} className="text-[#4A4A4A] hover:text-[#E94F9A]">
                    {isOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              {isOpen && (
                <div className="mt-4 pt-4 border-t border-[#FFF5F9] space-y-4">
                  {details.length > 0 && (
                    <div className="space-y-2 text-xs text-[#4A4A4A]">
                      {details.map((d, idx) => {
                        const val = d.value(item);
                        if (!val) return null;
                        return (
                          <div key={idx}>
                            <span className="font-semibold text-[#4B1E5A]">{d.label}: </span>
                            <span className="italic">{val}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Controls */}
                  <div className="flex flex-wrap items-center gap-2">
                    <select
                      value={item.status || "New"}
                      onChange={(e) => setStatus(item, e.target.value)}
                      data-testid={`status-select-${testId}-${item.id}`}
                      className="bg-white border border-[#E5E5E5] rounded-lg px-3 py-1.5 text-xs font-semibold text-[#4B1E5A]"
                    >
                      {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                    <button onClick={() => markRead(item, !item.read)} data-testid={`read-toggle-${testId}-${item.id}`} className="inline-flex items-center gap-1.5 border border-[#E5E5E5] text-[#4A4A4A] hover:bg-[#FFF5F9] rounded-full px-3 py-1.5 text-xs font-semibold">
                      {item.read ? <Mail className="h-3.5 w-3.5" /> : <MailOpen className="h-3.5 w-3.5" />}
                      {item.read ? "Mark Unread" : "Mark Read"}
                    </button>
                    <button onClick={() => toggleArchive(item)} data-testid={`archive-toggle-${testId}-${item.id}`} className="inline-flex items-center gap-1.5 border border-[#E5E5E5] text-[#4A4A4A] hover:bg-[#FFF5F9] rounded-full px-3 py-1.5 text-xs font-semibold">
                      {item.archived ? <ArchiveRestore className="h-3.5 w-3.5" /> : <Archive className="h-3.5 w-3.5" />}
                      {item.archived ? "Restore" : "Archive"}
                    </button>
                    {extraActions && extraActions(item)}
                  </div>

                  {/* Internal notes */}
                  <div className="space-y-2">
                    <label className="flex items-center gap-1.5 font-sans text-xs font-semibold text-[#4B1E5A]">
                      <StickyNote className="h-3.5 w-3.5 text-[#C8A96A]" /> Internal Notes
                    </label>
                    <textarea
                      rows={2}
                      value={noteDraft[item.id] ?? item.notes ?? ""}
                      onChange={(e) => setNoteDraft((n) => ({ ...n, [item.id]: e.target.value }))}
                      data-testid={`notes-${testId}-${item.id}`}
                      placeholder="Add a private note for your team..."
                      className="w-full bg-white border border-[#E5E5E5] rounded-lg px-3 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-[#E94F9A]/30"
                    />
                    <button onClick={() => saveNote(item)} data-testid={`save-notes-${testId}-${item.id}`} className="inline-flex items-center gap-1.5 bg-[#4B1E5A] text-white rounded-full px-4 py-1.5 text-xs font-semibold hover:bg-[#E94F9A] transition-colors">
                      <Save className="h-3.5 w-3.5" /> Save Note
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
